import "./ProductionSummary.css";
import { useEffect, useRef, useState } from "react";
import * as XLSX from "xlsx";


function ProductionSummary({ open, onClose }) {

  // ================= DRAG STATE =================
  const [sharing, setSharing] = useState(false);

  const [pos, setPos] = useState({
    x: 260,
    y: 120
  });

  const dragging = useRef(false);

  const offset = useRef({
    x: 0,
    y: 0
  });

  const startDrag = (e) => {

    dragging.current = true;

    offset.current = {
      x: e.clientX - pos.x,
      y: e.clientY - pos.y
    };
  };

  const onDrag = (e) => {

    if (!dragging.current) return;

    setPos({
      x: e.clientX - offset.current.x,
      y: e.clientY - offset.current.y
    });
  };

  const stopDrag = () => {
    dragging.current = false;
  };

  // ================= LIVE DATA =================

  const [data, setData] = useState({

    // ================= FEED =================

    crudeFeed: 0,

    // ================= PRODUCTS =================

    naphtha: 0,

    kerosene: 0,

    diesel: 0,

    residue: 0,

    fuelGas: 0,

    // ================= STEAM =================

    steamPressure: 0,

    steamTemperature: 0,

    // ================= TOTALIZERS =================

    totalCrudeFeed: 0,

    totalNaphtha: 0,

    totalKerosene: 0,

    totalDiesel: 0,

    totalResidue: 0,

    totalFuelGas: 0

  });

  // ================= TREND STORAGE =================

  const trend = useRef({

    crudeFeed: [],

    naphtha: [],

    kerosene: [],

    diesel: [],

    residue: [],

    fuelGas: [],

    steamPressure: [],

    steamTemperature: []

  });

  // ================= LIVE UPDATE ENGINE =================

  useEffect(() => {

    // ================= LOAD TOTALIZERS =================

    setData(prev => ({

      ...prev,

      totalCrudeFeed:
        Number(localStorage.getItem("totalCrudeFeed")) || 0,

      totalNaphtha:
        Number(localStorage.getItem("totalNaphtha")) || 0,

      totalKerosene:
        Number(localStorage.getItem("totalKerosene")) || 0,

      totalDiesel:
        Number(localStorage.getItem("totalDiesel")) || 0,

      totalResidue:
        Number(localStorage.getItem("totalResidue")) || 0,

      totalFuelGas:
        Number(localStorage.getItem("totalFuelGas")) || 0

    }));


    // ================= MAIN SCAN =================

    const interval = setInterval(() => {

      // ================= READ REFINERY TAGS =================

      const crudeFeed =
        Number(localStorage.getItem("FIC_1901")) || 0;

      const naphtha =
        Number(localStorage.getItem("FIC_2006")) || 0;

      const kerosene =
        Number(localStorage.getItem("FIC_2206")) || 0;

      const diesel =
        Number(localStorage.getItem("FIC_2408")) || 0;

      const residue =
        Number(localStorage.getItem("FIC_33010")) || 0;

      const fuelGas =
        Number(localStorage.getItem("FIC_2906")) || 0;

      // Steam (pressure & temperature only)

      const steamPressure =
        Number(localStorage.getItem("PT_3201")) || 0;

      const steamTemperature =
        Number(localStorage.getItem("TT_3201")) || 0;


      // ================= UPDATE STATE =================

      setData(prev => {

        const updated = {

          crudeFeed,

          naphtha,

          kerosene,

          diesel,

          residue,

          fuelGas,

          steamPressure,

          steamTemperature,


          // ================= TOTALIZERS =================

          totalCrudeFeed:
            prev.totalCrudeFeed + crudeFeed / 60,

          totalNaphtha:
            prev.totalNaphtha + naphtha / 60,

          totalKerosene:
            prev.totalKerosene + kerosene / 60,

          totalDiesel:
            prev.totalDiesel + diesel / 60,

          totalResidue:
            prev.totalResidue + residue / 60,

          totalFuelGas:
            prev.totalFuelGas + fuelGas / 60

        };


        // ================= SAVE =================

        localStorage.setItem(
          "totalCrudeFeed",
          updated.totalCrudeFeed
        );

        localStorage.setItem(
          "totalNaphtha",
          updated.totalNaphtha
        );

        localStorage.setItem(
          "totalKerosene",
          updated.totalKerosene
        );

        localStorage.setItem(
          "totalDiesel",
          updated.totalDiesel
        );

        localStorage.setItem(
          "totalResidue",
          updated.totalResidue
        );

        localStorage.setItem(
          "totalFuelGas",
          updated.totalFuelGas
        );

        return updated;

      });


      // ================= TRENDS =================

      const push = (key, value) => {

        const arr = trend.current[key];

        arr.push(value);

        if (arr.length > 20)
          arr.shift();

      };


      push("crudeFeed", crudeFeed);

      push("naphtha", naphtha);

      push("kerosene", kerosene);

      push("diesel", diesel);

      push("residue", residue);

      push("fuelGas", fuelGas);

      push("steamPressure", steamPressure);

      push("steamTemperature", steamTemperature);

    }, 1000);


    return () => clearInterval(interval);

  }, []);

  // ================= CONVERSIONS =================


  const BPD = (v) => v * 24;

  const M3DAY = (v) => v * 24;

  const KGHR = (v) => v;

  // ================= BAR SCALE =================

  const scale = (v, max) =>
    Math.min((v / max) * 100, 100);

  // ================= EXPORT EXCEL =================
  const exportExcel = () => {

    const sheet = [

      [
        "Parameter",
        "Current Value",
        "Units",
        "Accumulated"
      ],

      [
        "Crude Feed",
        data.crudeFeed,
        "BPM",
        data.totalCrudeFeed
      ],

      [
        "Naphtha",
        data.naphtha,
        "BPM",
        data.totalNaphtha
      ],

      [
        "Kerosene",
        data.kerosene,
        "BPM",
        data.totalKerosene
      ],

      [
        "Diesel",
        data.diesel,
        "BPM",
        data.totalDiesel
      ],

      [
        "Atmospheric Residue",
        data.residue,
        "BPM",
        data.totalResidue
      ],

      [
        "Fuel Gas",
        data.fuelGas,
        "Nm³/hr",
        data.totalFuelGas
      ],

      [
        "Steam Header Pressure",
        data.steamPressure,
        "MPa",
        "-"
      ],

      [
        "Steam Header Temperature",
        data.steamTemperature,
        "°C",
        "-"
      ]

    ];

    const ws =
      XLSX.utils.aoa_to_sheet(sheet);

    const wb =
      XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      wb,
      ws,
      "Refinery Production Summary"
    );

    XLSX.writeFile(
      wb,
      "NK_Refinery_Production_Summary.xlsx"
    );

  };

  // ================= PRINT =================

  const printWindow = () => {

    const printContent =
      document.querySelector(".prod-window");

    if (!printContent) return;

    // REMOVE FOOTER + CLOSE BUTTON
    const cloned =
      printContent.cloneNode(true);

    const footer =
      cloned.querySelector(".prod-footer");

    if (footer) footer.remove();

    const closeBtn =
      cloned.querySelector(".prod-close");

    if (closeBtn) closeBtn.remove();

    const printWin =
      window.open("", "", "width=1400,height=1000");

    printWin.document.write(`

    <html>

      <head>

        <title>
          NK Tech Production Summary
        </title>

        <style>

          *{
            box-sizing:border-box;
          }

          body{
            margin:0;
            padding:30px;
            background:white;
            color:#111827;
            font-family:Arial,sans-serif;
          }

          .prod-window{
            width:100%;
          }

          /* ================= HEADER ================= */

          .prod-header{
            text-align:center;
            border-bottom:3px solid #0f172a;
            padding-bottom:20px;
            margin-bottom:30px;
          }

          .prod-title{
            font-size:28px;
            font-weight:bold;
            color:#0f172a;
            letter-spacing:1px;
          }

          .prod-subtitle{
            font-size:18px;
            margin-top:10px;
            color:#475569;
          }

          /* ================= BODY ================= */

          .prod-body{
            width:100%;
          }

          .prod-text{
            margin-bottom:25px;
            font-size:15px;
            line-height:1.6;
          }

          /* ================= ROWS ================= */

          .prod-row{
            border:1px solid #cbd5e1;
            border-radius:10px;
            padding:15px;
            margin-bottom:15px;
            background:#f8fafc;
            page-break-inside:avoid;
          }

          .prod-tag{
            font-size:18px;
            font-weight:bold;
            color:#0f172a;
            margin-bottom:10px;
          }

          .prod-row div{
            margin-bottom:6px;
            font-size:14px;
          }

          /* ================= BAR ================= */

          .prod-bar{
            width:100%;
            height:12px;
            background:#e2e8f0;
            border-radius:20px;
            overflow:hidden;
            margin-top:12px;
          }

          .prod-bar div{
            height:100%;
            background:#0ea5e9;
            border-radius:20px;
          }

          /* ================= DISCLAIMER ================= */

          .prod-disclaimer{
            margin-top:30px;
            border:1px solid #cbd5e1;
            padding:20px;
            border-radius:10px;
            background:#f8fafc;
            line-height:1.8;
            font-size:14px;
          }

          /* ================= PRINT CLEANUP ================= */

          button{
            display:none !important;
          }

          @media print{

            body{
              padding:10px;
            }

            .prod-row{
              break-inside:avoid;
            }

          }

        </style>

      </head>

      <body>

        ${cloned.outerHTML}

      </body>

    </html>

  `);

    printWin.document.close();

    printWin.focus();

    setTimeout(() => {

      printWin.print();

      printWin.close();

    }, 700);

  };

  // ================= SHARE =================


  const shareWindow = async () => {

    try {

      const report = `

NK TECH INDUSTRIAL DCS AND DIGITAL TWIN SOLUTION
REFINERY PRODUCTION SUMMARY REPORT

----------------------------------------

CRUDE FEED

Current Rate : ${data.crudeFeed.toFixed(2)} BPM

Accumulated : ${data.totalCrudeFeed.toFixed(2)} BBL

----------------------------------------

NAPHTHA PRODUCTION

Current Rate : ${data.naphtha.toFixed(2)} BPM

Accumulated : ${data.totalNaphtha.toFixed(2)} BBL

----------------------------------------

KEROSENE PRODUCTION

Current Rate : ${data.kerosene.toFixed(2)} BPM

Accumulated : ${data.totalKerosene.toFixed(2)} BBL

----------------------------------------

DIESEL PRODUCTION

Current Rate : ${data.diesel.toFixed(2)} BPM

Accumulated : ${data.totalDiesel.toFixed(2)} BBL

----------------------------------------

ATMOSPHERIC RESIDUE

Current Rate : ${data.residue.toFixed(2)} BPM

Accumulated : ${data.totalResidue.toFixed(2)} BBL

----------------------------------------

FUEL GAS

Current Rate : ${data.fuelGas.toFixed(2)} Nm³/hr

Accumulated : ${data.totalFuelGas.toFixed(2)} Nm³

----------------------------------------

STEAM HEADER

Pressure : ${data.steamPressure.toFixed(2)} MPa

Temperature : ${data.steamTemperature.toFixed(2)} °C

----------------------------------------

REFINERY STATUS

Crude Feed :
${data.crudeFeed > 0 ? "RUNNING" : "STOPPED"}

Distillation Unit :
${data.crudeFeed > 0 ? "ONLINE" : "OFFLINE"}

----------------------------------------

DISCLAIMER

This refinery production report is generated by
the NK Tech Industrial DCS and Digital Twin
System for process monitoring, simulation,
training and educational purposes only.

Values shown are derived from simulated or
configured process calculations and shall not
be used for custody transfer, commercial
accounting or certified plant reporting.

Generated by

NK TECH INDUSTRIAL DCS AND DIGITAL TWIN SOLUTION

`;

      // ================= NATIVE SHARE =================

      if (navigator.share) {

        await navigator.share({

          title:
            "NK Tech Production Summary",

          text: report

        });

      } else {

        // ================= FALLBACK =================

        await navigator.clipboard.writeText(report);

        alert(
          "Production Summary copied to clipboard."
        );
      }

    } catch (err) {

      console.log("Share Error:", err);

    }

  };

  // ================= CLOSE CONDITION =================

  if (!open) return null;

  return (

    <div
      className="prod-overlay"
      onMouseMove={onDrag}
      onMouseLeave={stopDrag}
      onMouseUp={stopDrag}
    >

      <div
        className="prod-window"
        style={{
          left: pos.x,
          top: pos.y
        }}
      >

        {/* ================= HEADER ================= */}

        <div
          className="prod-header"
          onMouseDown={startDrag}
        >

          <div className="prod-title">
            NK TECH INDUSTRIAL DCS AND DIGITAL TWIN SOLUTION
          </div>

          <div className="prod-subtitle">
            PRODUCTION SUMMARY
          </div>

          <button
            className="prod-close"
            onClick={onClose}
          >
            ✕
          </button>

        </div>

        {/* ================= BODY ================= */}

        <div className="prod-body">

          <p className="prod-text">
            Live refinery production summary generated from
            the NK Tech Refinery Digital Twin Distributed
            Control System (DCS). The report displays the
            current crude feed rate, refined product
            production rates, utility conditions and
            accumulated production totals based on live
            process tags.
          </p>

          {/* ================= REFINERY PRODUCTS ================= */}

          {[
            [
              "Crude Feed",
              data.crudeFeed,
              data.totalCrudeFeed,
              "BPM"
            ],

            [
              "Naphtha",
              data.naphtha,
              data.totalNaphtha,
              "BPM"
            ],

            [
              "Kerosene",
              data.kerosene,
              data.totalKerosene,
              "BPM"
            ],

            [
              "Diesel",
              data.diesel,
              data.totalDiesel,
              "BPM"
            ],

            [
              "Atmospheric Residue",
              data.residue,
              data.totalResidue,
              "BPM"
            ],

            [
              "Fuel Gas",
              data.fuelGas,
              data.totalFuelGas,
              "Nm³/hr"
            ]

          ].map(([tag, value, total, unit]) => (

            <div
              className="prod-row"
              key={tag}
            >

              <div className="prod-tag">

                {tag}

              </div>

              <div>

                Current Rate :

                {" "}

                {Number(value).toFixed(2)}

                {" "}

                {unit}

              </div>

              <div>

                Accumulated :

                {" "}

                {Number(total).toFixed(2)}

              </div>

              <div className="prod-bar">

                <div
                  style={{
                    width: `${scale(value, 100)}%`
                  }}
                />

              </div>

            </div>

          ))}

          {/* ================= STEAM SYSTEM ================= */}

          <div className="prod-row">

            <div className="prod-tag">
              Steam Header
            </div>

            <div>
              Pressure :
              {" "}
              {Number(data.steamPressure).toFixed(2)}
              {" "}
              MPa
            </div>

            <div>
              Temperature :
              {" "}
              {Number(data.steamTemperature).toFixed(2)}
              {" "}
              °C
            </div>

            <div className="prod-bar">

              <div
                style={{
                  width: `${scale(data.steamPressure, 25)}%`
                }}
              />

            </div>

          </div>


          {/* ================= REFINERY STATUS ================= */}

          <div className="prod-row">

            <div className="prod-tag">
              Refinery Operating Status
            </div>

            <div>

              CDU Feed :

              {" "}

              {data.crudeFeed > 0
                ? "RUNNING"
                : "STOPPED"}

            </div>

            <div>

              Product Recovery :

              {" "}

              {(

                data.naphtha +

                data.kerosene +

                data.diesel +

                data.residue

              ).toFixed(2)}

              {" "}
              BPM

            </div>

            <div>

              Fuel Gas :

              {" "}

              {Number(data.fuelGas).toFixed(2)}

              {" "}
              Nm³/hr

            </div>

          </div>

          {/* ================= DISCLAIMER ================= */}

          <div className="prod-disclaimer">

            <strong>Disclaimer:</strong>

            {" "}

            This production data is simulated
            for training, process monitoring,
            and operational familiarization only.
            It does not represent certified
            fiscal metering or custody transfer
            values.

          </div>

        </div>

        {/* ================= FOOTER ================= */}

        <div className="prod-footer">

          <button onClick={exportExcel}>
            Export Excel
          </button>

          <button
            onClick={shareWindow}
            disabled={sharing}
          >
            {sharing
              ? "Sharing..."
              : "Share"}
          </button>

          <button onClick={printWindow}>
            Print PDF
          </button>

          <button
            onClick={() => {

              localStorage.removeItem("totalCrudeFeed");

              localStorage.removeItem("totalNaphtha");

              localStorage.removeItem("totalKerosene");

              localStorage.removeItem("totalDiesel");

              localStorage.removeItem("totalResidue");

              localStorage.removeItem("totalFuelGas");

              setData(prev => ({

                ...prev,

                totalCrudeFeed: 0,

                totalNaphtha: 0,

                totalKerosene: 0,

                totalDiesel: 0,

                totalResidue: 0,

                totalFuelGas: 0

              }));

              alert("Refinery production totals have been reset.");

            }}
          >
            Reset Totals
          </button>
        </div>

      </div>

    </div>
  );
}

export default ProductionSummary;